import React, { useState, useEffect, useRef } from "react";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./UserFormsList.css";
import usePageTitle from "../../usePageTitle";
import UserFormsListHeaderComponent from "./UserFormsListHeaderComponent";
import UserFormsListDatatableComponent from "./UserFormsListDatatableComponent";
import { FilterMatchMode } from "primereact/api";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";

import { fetchUserForms } from "./api";

const UserFormsList = () => {
  usePageTitle("My Assigned Forms List - SkeyeForm™");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(24);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const forms = await fetchUserForms();
        if (Array.isArray(forms)) {
          // Only show active forms (not soft deleted)
          const activeForms = forms.filter((form) => !form.softDelete);
          setTotalRecords(activeForms.length);
          setData(activeForms);
          setFilteredData(activeForms.slice(first, first + rows));
        } else {
          console.error("Unexpected response format");
        }
      } catch (error) {
        console.error("Error fetching user forms:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [first, rows]);

  useEffect(() => {
    if (Array.isArray(data)) {
      let filtered = data.filter((project) => {
        const formattedDate = project.createdAt
          ? new Date(project.createdAt).toLocaleString("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })
          : "";
        return (
          (project.title &&
            project.title
              .toLowerCase()
              .includes(globalFilterValue.toLowerCase())) ||
          (formattedDate && formattedDate.includes(globalFilterValue)) ||
          (project.status &&
            project.status.toString().includes(globalFilterValue))
        );
      });

      if (sortField) {
        filtered = filtered.sort((a, b) => {
          const valueA = a[sortField];
          const valueB = b[sortField];
          let result = 0;

          if (valueA < valueB) result = -1;
          else if (valueA > valueB) result = 1;

          return sortOrder * result;
        });
      }
      setFilteredData(filtered.slice(first, first + rows));
      setTotalRecords(filtered.length);
    }
  }, [globalFilterValue, first, rows, data, sortField, sortOrder]);

  const handleFormDeleted = (deletedFormId, deleteType) => {
    if (deleteType === "hard") {
      const updatedData = data.filter((form) => form.id !== deletedFormId);
      setData(updatedData);

      const updatedFilteredData = filteredData.filter(
        (form) => form.id !== deletedFormId
      );
      setFilteredData(updatedFilteredData);
      setTotalRecords(updatedData.length);
    } else {
      const updatedData = data.filter((form) => form.id !== deletedFormId);
      setData(updatedData);

      const updatedFilteredData = filteredData.filter(
        (form) => form.id !== deletedFormId
      );
      setFilteredData(updatedFilteredData);
      setTotalRecords(updatedData.length);
    }
  };

  const onPageChange = (e) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
    setFirst(0);
  };

  const clearFilter = () => {
    setGlobalFilterValue("");
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
  };

  const onSort = (e) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder);
  };

  const header = (
    <UserFormsListHeaderComponent
      globalFilterValue={globalFilterValue}
      onGlobalFilterChange={onGlobalFilterChange}
      clearFilter={clearFilter}
    />
  );

  return (
    <div className="p-grid p-dir-col table-page">
      <Toast ref={toast} />
      <div className="p-col title">
        <h1>My Assigned Forms List</h1>
        <div className="title-bar"></div>
      </div>
      {loading ? (
        <div className="loader-container">
          <ProgressSpinner
            height={40}
            width={40}
            color="#D97A07"
            wrapperStyle={{ display: "inline-block", verticalAlign: "middle" }}
            wrapperClass=""
            visible={true}
            ariaLabel="oval-loading"
            secondaryColor="#D97A07"
            strokeWidth={2}
            strokeWidthSecondary={2}
          />
          <span
            style={{
              marginLeft: "10px",
              verticalAlign: "middle",
              fontSize: "20px",
              fontFamily: "Poppins",
            }}
          >
            Fetching Data (This Might Take a Minute)...
          </span>
        </div>
      ) : (
        <UserFormsListDatatableComponent
          data={filteredData}
          filters={filters}
          setFilters={setFilters}
          header={header}
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          onPageChange={onPageChange}
          emptyMessage="No forms assigned to you"
          onFormDeleted={handleFormDeleted}
          onSort={onSort}
          sortField={sortField}
          sortOrder={sortOrder}
        />
      )}
    </div>
  );
};

export default UserFormsList;
